import type { HomeTask } from './tasks';
import type { Batch } from '../BatchTracking/types';

type Placed = { entry: 'task'; task: HomeTask } | { entry: 'batch'; batch: Batch };

export type TimelineEntry = (Placed & { gap?: string }) | { entry: 'now' };

const MINUTE_MS = 60_000;

const startOf = (placed: Placed): number => Date.parse(placed.entry === 'task' ? placed.task.startsAt : placed.batch.createdAt);
const idOf = (placed: Placed): string => (placed.entry === 'task' ? placed.task.id : placed.batch.id);
const endOf = (task: HomeTask): number => Date.parse(task.endsAt ?? task.startsAt);

// Whole minutes from the end of one task to the start of the next; 0 when they touch or overlap.
export const gapMinutes = (from: HomeTask, to: HomeTask): number =>
  Math.max(0, Math.round((Date.parse(to.startsAt) - endOf(from)) / MINUTE_MS));

export const formatGap = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) {
    return `${rest} min`;
  }
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};

// `now`, or the newest batch's creation time when that is later: a batch is a record, so it has started whatever this clock says.
export const effectiveNow = (now: Date, batches: Batch[]): Date =>
  new Date(Math.max(now.getTime(), ...batches.map(({ createdAt }) => Date.parse(createdAt))));

// Tasks and batches in time order, a task with the gap to the task right after it, and the now marker after the last entry that has started.
export const buildTimeline = (tasks: HomeTask[], batches: Batch[], now: Date): TimelineEntry[] => {
  const placed: Placed[] = [
    ...tasks.map((task): Placed => ({ entry: 'task', task })),
    ...batches.map((batch): Placed => ({ entry: 'batch', batch }))
  ].toSorted((a, b) => startOf(a) - startOf(b) || idOf(a).localeCompare(idOf(b)));
  const entries: TimelineEntry[] = placed.map((item, at) => {
    const next = placed[at + 1];
    if (item.entry === 'task' && next?.entry === 'task') {
      const minutes = gapMinutes(item.task, next.task);
      return minutes > 0 ? { ...item, gap: formatGap(minutes) } : item;
    }
    return item;
  });
  const upcoming = placed.findIndex((item) => startOf(item) > now.getTime());
  entries.splice(upcoming === -1 ? entries.length : upcoming, 0, { entry: 'now' });
  return entries;
};
